import { useState } from "react";
import { getCoinName } from "../utils/getCoinName";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  useIonModal
} from "@ionic/react";
import GradientLineChart from "./GradientLineChart";
import RangeSelector from "./RangeSelector";
import { arrowBack } from "ionicons/icons";
import getCoinImage from "../utils/getCoinImage";
import ConfirmationPage from "../pages/Confirmation";
import {
  transformTimestamp,
  useIndicatorsStore
} from "../stores/indicators.store";
import RSIReading from "./RSIReading";

interface Props {
  coin: any;
  coinId: string;
  interval: string;
  availableQuoteBalance: number;
  onDismiss: (confirm: boolean, buyAmount?: number) => () => void;
}

const ManualBuy: React.FC<Props> = ({
  coin,
  coinId,
  interval,
  availableQuoteBalance,
  onDismiss
}) => {
  const [selectedChart, setSelectedChart] = useState("RSI");
  const symbolData = useIndicatorsStore(state =>
    state.getDataForSymbol(coin?.symbol)
  );
  const rsiReadings = symbolData?.rsiReadings;
  const priceReadings = symbolData?.priceReadings;
  const [buyAmount, setBuyAmount] = useState(
    availableQuoteBalance < 30 ? availableQuoteBalance : 30
  );

  const handleSegmentChange = (value: any) => {
    setSelectedChart(value);
    //   setIsLoading(true);
  };

  const handleBuyAmountChange = (value: any) => {
    const numericValue = Number(value); // Ensure the value is a number
    const amount = isNaN(numericValue)
      ? 10
      : Math.min(Math.max(numericValue, 10), availableQuoteBalance);
    setBuyAmount(amount);
  };

  const [presentConfirm, dismissConfirm] = useIonModal(ConfirmationPage, {
    onDismiss: (confirm: boolean) => {
      if (confirm) {
        onDismiss(true, buyAmount);
      }
      dismissConfirm();
    },
    title: "Confirm Manual Buy",
    lottieAnimation: "botCongratsAnimation",
    confirmText: "Confirm",
    contentHtml: `
    <div class="p-8 font-bold text-center">
      Are you sure you want to buy ${buyAmount} USDT worth ${(
      buyAmount / coin?.price
    ).toFixed(8)} of ${coinId}?
    </div>
    `,
    cssClass: "transparent-modal"
  });

  const coinName = getCoinName(coinId);
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => onDismiss(false)}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Manual Buy</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="flex items-center font-bold text-2xl my-2 text-white text-center">
          <img
            src={getCoinImage(coinId)}
            alt={coin}
            className="w-[48px] h-[48px] mx-4"
          />
          {`${coinName}`}
        </div>
        <div className="flex justify-between pr-6">
          {coin && coin.price && (
            <div className="text-primary-20 text-xl px-4 pb-2">
              {`1 ${coinId} = ${coin.price} USDT`}
            </div>
          )}
          <div>
            <span className="font-lg"> RSI </span>
            <RSIReading symbol={coin.symbol} interval={interval || "15m"} />
          </div>
        </div>
        <RangeSelector
          title="Buy Amount"
          min={10}
          max={availableQuoteBalance}
          step={1}
          unit=" USDT"
          value={buyAmount}
          onValueChange={value => handleBuyAmountChange(value)}
        />
        <IonButton
          className="h-16 text-base p-2"
          color="primary"
          expand="block"
          onClick={() => presentConfirm()}
        >
          Buy
        </IonButton>
        <IonCard className="p-2 bg-primary-80">
          <IonSegment
            value={selectedChart}
            onIonChange={e => handleSegmentChange(e.detail.value)}
          >
            <IonSegmentButton value="RSI">
              <IonLabel>RSI</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="PRICE">
              <IonLabel>Price</IonLabel>
            </IonSegmentButton>
          </IonSegment>
          <div>
            {selectedChart === "PRICE" && (
              <GradientLineChart
                title="Price"
                key="price-chart"
                data={
                  priceReadings?.map((reading: any) => [
                    transformTimestamp(reading.timestamp),
                    reading.price
                  ]) as [string, number][]
                }
                disableFilter
                considerTimezone
              />
            )}
            {selectedChart === "RSI" && (
              <GradientLineChart
                key="rsi-chart"
                title="RSI"
                data={
                  rsiReadings?.map((reading: any) => [
                    transformTimestamp(reading.timestamp),
                    reading.rsi
                  ]) as [string, number][]
                }
                disableFilter
                considerTimezone
              />
            )}
          </div>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default ManualBuy;
